import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

/**
 * ThemeToggle — Audit Editorial design-system switcher.
 *
 * 2 themes:
 *   "editorial-light" — light canvas + Plex Serif
 *   "editorial-dark"  — dark canvas + Plex Serif
 *
 * Persists via localStorage `adjudon_theme`. Sets `<html data-theme="...">`.
 * Legacy stored value `"obsidian"` (v3) is silently migrated to
 * `"editorial-dark"` so existing users don't see a flash of unstyled chrome.
 */

export type AdjudonTheme = "editorial-light" | "editorial-dark";

const STORAGE_KEY = "adjudon_theme";
const DEFAULT_THEME: AdjudonTheme = "editorial-dark";

function readStoredTheme(): AdjudonTheme {
  if (typeof window === "undefined") return DEFAULT_THEME;
  // localStorage access can throw SecurityError in some embedded contexts
  // (sandboxed iframes, private-browsing with strict settings). Falling back
  // to the default theme is preferred to crashing the entire app on boot.
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "editorial-light" || raw === "editorial-dark") {
      return raw;
    }
    // Legacy migration — v3 obsidian users get the closest editorial equivalent.
    if (raw === "obsidian") {
      try { window.localStorage.setItem(STORAGE_KEY, "editorial-dark"); } catch { /* quota / SecurityError — non-fatal */ }
      return "editorial-dark";
    }
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return DEFAULT_THEME;
}

function applyTheme(theme: AdjudonTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

const THEME_ORDER: AdjudonTheme[] = ["editorial-light", "editorial-dark"];

const THEME_META: Record<AdjudonTheme, { label: string; icon: typeof Sun; description: string }> = {
  "editorial-light": {
    label: "Editorial Light",
    icon: Sun,
    description: "Light canvas + Plex Serif",
  },
  "editorial-dark": {
    label: "Editorial Dark",
    icon: Moon,
    description: "Dark canvas + Plex Serif",
  },
};

export function ThemeToggle() {
  const [theme, setTheme] = useState<AdjudonTheme>(() => readStoredTheme());

  // Apply theme on mount + on every change.
  useEffect(() => {
    applyTheme(theme);
    if (typeof window !== "undefined") {
      try { window.localStorage.setItem(STORAGE_KEY, theme); } catch {
        /* quota / SecurityError — theme still applies via setAttribute,
           just won't persist across reloads. Non-fatal. */
      }
    }
  }, [theme]);

  const cycleTheme = () => {
    const idx = THEME_ORDER.indexOf(theme);
    const next = THEME_ORDER[(idx + 1) % THEME_ORDER.length];
    setTheme(next);
  };

  const meta = THEME_META[theme];
  const Icon = meta.icon;

  // Icon-only header button — matches the visual register of the
  // sibling Bug-report and Bell (NotificationCenter) icons. Tooltip
  // carries the full label for hover + screen-reader users.
  return (
    <button
      type="button"
      onClick={cycleTheme}
      data-testid="theme-toggle"
      aria-label={`Theme: ${meta.label}. Click to toggle theme.`}
      title={`${meta.label} — ${meta.description}\nClick to toggle light/dark.`}
      className="relative p-1.5 sm:p-2 hover:bg-surface-2 border border-transparent hover:border-border transition-colors group"
    >
      <Icon
        className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground group-hover:text-foreground transition-colors"
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * Initialiser — call this once at app boot (e.g. in main.tsx) BEFORE
 * the React tree renders, so we don't get a flash of unstyled theme.
 */
export function initThemeFromStorage(): void {
  const theme = readStoredTheme();
  applyTheme(theme);
}
